import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ProjectSection } from '@/types/project.types';

interface ProjectItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  subtotal: number;
}

interface ProjectState {
  currentProject: {
    name: string;
    sections: ProjectSection[];
    totalCost: number;
  };
  setProjectName: (name: string) => void;
  addSection: (section: ProjectSection) => void;
  updateSection: (sectionId: string, updates: Partial<ProjectSection>) => void;
  removeSection: (sectionId: string) => void;
  addItem: (sectionId: string, item: ProjectItem) => void;
  updateItem: (sectionId: string, itemId: string, updates: Partial<ProjectItem>) => void;
  removeItem: (sectionId: string, itemId: string) => void;
  calculateTotal: () => number;
  clearProject: () => void;
}

const initialState: Omit<ProjectState, 'setProjectName' | 'addSection' | 'updateSection' | 'removeSection' | 'addItem' | 'updateItem' | 'removeItem' | 'calculateTotal' | 'clearProject'> = {
  currentProject: {
    name: '',
    sections: [],
    totalCost: 0,
  },
};

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      ...initialState,
      setProjectName: (name) =>
        set((state) => ({
          currentProject: { ...state.currentProject, name },
        })),
      addSection: (section) =>
        set((state) => ({
          currentProject: {
            ...state.currentProject,
            sections: [...state.currentProject.sections, section],
          },
        })),
      updateSection: (sectionId, updates) =>
        set((state) => ({
          currentProject: {
            ...state.currentProject,
            sections: state.currentProject.sections.map((section) =>
              section.id === sectionId ? { ...section, ...updates } : section
            ),
          },
        })),
      removeSection: (sectionId) =>
        set((state) => ({
          currentProject: {
            ...state.currentProject,
            sections: state.currentProject.sections.filter((section) => section.id !== sectionId),
          },
        })),
      addItem: (sectionId, item) =>
        set((state) => ({
          currentProject: {
            ...state.currentProject,
            sections: state.currentProject.sections.map((section) =>
              section.id === sectionId
                ? { ...section, items: [...(section.items || []), item] }
                : section
            ),
          },
        })),
      updateItem: (sectionId, itemId, updates) =>
        set((state) => ({
          currentProject: {
            ...state.currentProject,
            sections: state.currentProject.sections.map((section) =>
              section.id === sectionId
                ? {
                    ...section,
                    items: section.items?.map((item) =>
                      item.id === itemId ? { ...item, ...updates } : item
                    ),
                  }
                : section
            ),
          },
        })),
      removeItem: (sectionId, itemId) =>
        set((state) => ({
          currentProject: {
            ...state.currentProject,
            sections: state.currentProject.sections.map((section) =>
              section.id === sectionId
                ? {
                    ...section,
                    items: section.items?.filter((item) => item.id !== itemId),
                  }
                : section
            ),
          },
        })),
      calculateTotal: () => {
        const { currentProject } = get();
        return currentProject.sections.reduce(
          (total, section) =>
            total +
            (section.items?.reduce((sectionTotal, item) => sectionTotal + item.subtotal, 0) || 0),
          0
        );
      },
      clearProject: () => set(initialState),
    }),
    {
      name: 'project-storage',
      partialize: (state) => ({
        currentProject: state.currentProject,
      }),
    }
  )
); 